import React from 'react';
import ReactDOM from 'react-dom/client';
import App from '@/App';
import ErrorBoundary from '@/components/ErrorBoundary';
import reportWebVitals from '@/reportWebVitals';
import { isTunnelHost } from '@/modules/config';

// Ensure root element exists
const rootElement = document.getElementById('root');
if (!rootElement) {
  // eslint-disable-next-line no-console
  console.error('❌ Root element not found!');
  document.body.innerHTML =
    '<div style="padding: 20px; font-family: sans-serif;"><h1>Error</h1><p>Root element not found. Please check the HTML.</p></div>';
} else {
  try {
    // eslint-disable-next-line no-console
    console.log('🚀 Starting React app initialization...');

    // Check if createRoot is available (React 18+)
    if (typeof ReactDOM.createRoot === 'function') {
      const root = ReactDOM.createRoot(rootElement);
      root.render(
        <React.StrictMode>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </React.StrictMode>
      );
      // eslint-disable-next-line no-console
      console.log('✅ App rendered successfully with createRoot');

      // Mark app as loaded for diagnostic script
      if (typeof window !== 'undefined') {
        // @ts-expect-error window.__APP_LOADED__ is a global variable
        window.__APP_LOADED__ = true;
      }
    } else {
      // Fallback for older React versions (shouldn't happen with React 19, but just in case)
      throw new Error(
        'ReactDOM.createRoot is not available. This should not happen with React 19.'
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('❌ Failed to render app:', error);
    const errorMessage =
      error instanceof Error ? error.toString() : String(error);
    const errorStack = error instanceof Error && error.stack ? error.stack : '';
    rootElement.innerHTML =
      '<div style="padding: 20px; font-family: sans-serif;">' +
      '<h1 style="color: #d32f2f;">Initialization Error</h1>' +
      '<p>Failed to initialize the app. Please refresh the page.</p>' +
      '<details style="margin-top: 1rem;">' +
      '<summary style="cursor: pointer; font-weight: bold;">Error Details</summary>' +
      '<pre style="background: #f5f5f5; padding: 10px; border-radius: 4px; overflow: auto; margin-top: 0.5rem; font-size: 0.9em;">' +
      errorMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;') +
      (errorStack
        ? '\n\nStack:\n' +
          errorStack.replace(/</g, '&lt;').replace(/>/g, '&gt;')
        : '') +
      '</pre>' +
      '</details>' +
      '<button onclick="window.location.reload()" style="margin-top: 10px; padding: 10px 20px; font-size: 16px; background: #3880ff; color: white; border: none; border-radius: 4px; cursor: pointer;">' +
      'Reload Page</button>' +
      '</div>';
  }
}

// Dev tunnels (ngrok, Cloudflare quick tunnels) share a small free bandwidth
// quota, and a registered service worker generates constant background traffic
// (precaching, update checks) that burns through it. Block SW registration
// when served from any known tunnel host. The tunnel-host list/check is shared
// with the config module so SW gating and API-base detection stay in sync.
const onTunnelHost = isTunnelHost(window.location.hostname);

// Escape hatch for testing PWA install behavior (e.g. WebAPK minting and the
// manifest `launch_handler`) over a tunnel: opt back into the service worker
// by running `localStorage.setItem('snowball:allow-sw-on-tunnel', '1')` and
// reloading. Requires a production build (the dev server emits no SW).
const allowServiceWorkerOnTunnel = (() => {
  try {
    return localStorage.getItem('snowball:allow-sw-on-tunnel') === '1';
  } catch {
    return false;
  }
})();
const blockTunnelServiceWorker = onTunnelHost && !allowServiceWorkerOnTunnel;

// Unregister any existing dev service workers to prevent reload loops
// Dev service workers try to connect to HMR WebSocket which can fail in PWA mode
// This must run early, before any service worker registration attempts
if ('serviceWorker' in navigator) {
  // Immediately unregister all service workers in dev mode to prevent reload loops
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      // Unregister ALL service workers in dev mode to prevent conflicts
      // Dev service workers have 'dev-sw' in their scope or script URL
      const scriptURL =
        registration.active?.scriptURL ||
        registration.waiting?.scriptURL ||
        registration.installing?.scriptURL ||
        '';
      // Check if this is a dev service worker
      const isDevSW =
        registration.scope.includes('dev-sw') ||
        scriptURL.includes('dev-sw') ||
        scriptURL.includes('/dev-sw.js');

      // On tunnel hosts, tear down any existing SW so it stops generating
      // background traffic against the tunnel's bandwidth quota.
      if (isDevSW || blockTunnelServiceWorker) {
        registration.unregister().catch(() => {
          // Ignore unregistration errors
        });
      }
    });
  });

  // Prevent any new dev service worker registrations
  // Override the register method to block dev-sw.js registration
  const originalRegister = navigator.serviceWorker.register.bind(
    navigator.serviceWorker
  );
  navigator.serviceWorker.register = function (
    scriptURL: string | URL,
    options?: RegistrationOptions
  ) {
    const urlString =
      typeof scriptURL === 'string' ? scriptURL : scriptURL.toString();
    // Block all service worker registration on tunnel hosts to conserve the
    // tunnel's limited bandwidth quota.
    if (blockTunnelServiceWorker) {
      // eslint-disable-next-line no-console
      console.warn(
        'Blocked service worker registration on tunnel host to conserve bandwidth'
      );
      return Promise.reject(
        new Error('Service worker registration blocked on tunnel host')
      );
    }
    // Block dev service worker registration to prevent reload loops
    if (urlString.includes('dev-sw') || urlString.includes('/dev-sw.js')) {
      // eslint-disable-next-line no-console
      console.warn(
        'Blocked dev service worker registration to prevent reload loops'
      );
      return Promise.reject(
        new Error(
          'Dev service worker registration blocked to prevent reload loops'
        )
      );
    }
    // Allow other registrations (for production builds)
    return originalRegister(scriptURL, options);
  };
}

// Service worker registration is handled by VitePWA plugin via registerSW.js
// No manual registration needed here (and dev service worker is disabled in vite.config.ts)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
